import { useState } from 'react';
import { api } from '../lib/api.js';
import { inputStyle } from '../lib/ui.js';
import { todayStr } from '../lib/format.js';

// Round 32: bulk task import — managers download a CSV template, fill in
// rows for one or many tasks, upload back. Mirrors the recipe bulk-import
// pattern: parse-and-create with per-row outcomes for review.
export function TaskBulkImportModal({ user, onClose, onImported }) {
  const headers = { 'x-user-id': String(user.id) };
  const [csvText, setCsvText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  async function downloadTemplate() {
    setErr('');
    try {
      const res = await fetch('/api/tasks/import-template', { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      const blob = new Blob([text], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `task-import-template-${todayStr()}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(e.message); }
  }
  async function onFile(e) {
    const f = e.target.files[0]; if (!f) return;
    const text = await f.text();
    setCsvText(text);
  }
  async function importNow() {
    if (!csvText.trim()) { setErr('Paste or upload a CSV first.'); return; }
    setBusy(true); setErr(''); setResult(null);
    try {
      const r = await api('POST', '/api/tasks/bulk-import', { csv: csvText }, headers);
      setResult(r);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div onClick={onClose} className="kh-modal-overlay"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} className="kh-modal-shell"
        style={{ background: '#fff', borderRadius: 10, padding: 20, maxWidth: 720, width: '100%', maxHeight: '92vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Bulk import tasks</div>
          <button onClick={onClose} className="btn-secondary" style={{ fontSize: 12 }}>Close</button>
        </div>
        <div style={{ fontSize: 12, color: '#37474f', lineHeight: 1.5, marginBottom: 12 }}>
          Step 1: Download the template — already pre-filled with three example rows.
          <br />Step 2: Edit / add rows for the tasks you want to create.
          <br />Step 3: Save as CSV and upload below.
          <div style={{ marginTop: 8, padding: 8, background: '#f5f5f5', borderRadius: 4, fontSize: 11, fontFamily: 'monospace' }}>
            <div><b>location</b>: <code>Bellevue</code> / <code>Broadway</code> / <code>Bristol</code> / <code>JT Connell</code> — or <code>all</code> for chain-wide</div>
            <div><b>time_period</b>: <code>Opening</code> / <code>Morning</code> / <code>Midday</code> / <code>Afternoon</code> / <code>Closing</code> (must match an existing period)</div>
            <div><b>recurrence</b>: <code>daily</code> / <code>weekly</code> / <code>monthly</code></div>
            <div><b>recurrence_days</b> (weekly only): <code>mon|tue|wed|thu|fri|sat|sun</code></div>
            <div><b>assigned_role</b>: <code>Barista</code> / <code>Shift Lead</code> / <code>Manager</code> / <code>Owner</code></div>
            <div><b>due_time</b>: 24-hour <code>HH:MM</code> (e.g., <code>06:30</code>); blank = no specific time</div>
            <div><b>sop_steps</b> (optional): <code>step 1||step 2||step 3</code> — fills an inline SOP for the task</div>
            <div style={{ marginTop: 4, color: '#a16207' }}>Duplicate-protection: rows whose (title + period + location) already exist as an active task are skipped.</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <button onClick={downloadTemplate}
            style={{ padding: '7px 16px', fontSize: 12, fontWeight: 700,
                      background: 'var(--gold)', color: '#fff', border: 'none', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit' }}>
            ↓ Download template
          </button>
          <input type="file" accept=".csv,text/csv" onChange={onFile} style={{ fontSize: 12 }} />
        </div>
        <textarea value={csvText} onChange={(e) => setCsvText(e.target.value)}
          rows={10} placeholder="…or paste CSV here"
          style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 11, resize: 'vertical', width: '100%' }} />
        {err && <div style={{ fontSize: 11, color: '#c62828', marginTop: 6 }}>{err}</div>}
        {result && (
          <div style={{ marginTop: 10, padding: 10, background: '#f5f5f5', borderRadius: 4, fontSize: 12 }}>
            <div><b style={{ color: '#1b5e20' }}>✓ Imported {result.imported}</b></div>
            {result.skipped?.length > 0 && (
              <details style={{ marginTop: 6 }}>
                <summary style={{ cursor: 'pointer', color: '#a16207' }}>Skipped {result.skipped.length}</summary>
                <ul style={{ margin: '6px 0 0 16px', fontSize: 11 }}>
                  {result.skipped.map((s, i) => <li key={i}>row {s.row} ({s.title}): {s.reason}</li>)}
                </ul>
              </details>
            )}
            {result.errors?.length > 0 && (
              <details style={{ marginTop: 6 }}>
                <summary style={{ cursor: 'pointer', color: '#c62828' }}>Errors {result.errors.length}</summary>
                <ul style={{ margin: '6px 0 0 16px', fontSize: 11 }}>
                  {result.errors.map((e, i) => <li key={i}>row {e.row}: {e.error}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}
        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {result?.imported > 0 ? (
            <button onClick={onImported}
              style={{ padding: '7px 16px', fontSize: 12, fontWeight: 700,
                        background: 'var(--gold)', color: '#fff', border: 'none', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit' }}>
              Done
            </button>
          ) : (
            <button onClick={importNow} disabled={busy || !csvText.trim()}
              style={{ padding: '7px 16px', fontSize: 12, fontWeight: 700,
                        background: 'var(--gold)', color: '#fff', border: 'none', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
                        opacity: (busy || !csvText.trim()) ? 0.5 : 1 }}>
              {busy ? 'Importing…' : 'Import'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
